import type { ModSetting, ModSettingPreset } from '../api/mods';
import type { ModExclusionsCount } from './mod-exclusions';
import { t } from '../i18n';

// ============================================================
// Рандомизация настроек мода — общая логика для инструментов
// «Выбор карты и режима» (/tools/random) и «Менеджер матча» (/tools/match).
// Параметры с randomize=false по умолчанию выключены: показываются
// отдельно с дефолтным значением; клик включает параметр и даёт
// ему случайное значение. Остальные рандомизируются сразу.
// Пресеты подставляют сохранённые комбинации значений (показываются только
// параметры, указанные в пресете; режим переключается на «Пресет»).
// Исключения из рандомизации (правит модератор в окне «Исключения»):
// параметр с disabled=true не участвует в розыгрыше вовсе,
// значения из excluded_values не выпадают.
// ============================================================

/** Значение одного параметра в результате розыгрыша. */
export interface ModSettingValue {
    name: string;
    value: string;
    /** false — параметр выключен и стоит на дефолтном значении. */
    enabled: boolean;
}

type ModSettingParam = ModSetting['params'][number];

/** Откуда взялись значения: случайный розыгрыш параметров или готовый пресет. */
export type ModSettingMode = 'random' | 'preset';

/** Разrolled настройки мода: modId + значения параметров. */
export interface ModSettingRoll {
    modId: number;
    params: ModSettingValue[];
    /** Режим розыгрыша (без пресетов всегда random). */
    mode?: ModSettingMode;
    /** Имя применённого пресета в режиме preset. */
    presetName?: string | null;
}

/** Дефолт параметра: default_value, иначе первое значение, иначе «—». */
export function settingFallback(param: ModSettingParam): string {
    return param.default_value ?? param.values[0] ?? '—';
}

/** Значения, доступные розыгрышу: без исключённых модератором. */
export function allowedParamValues(param: ModSettingParam): string[] {
    const excluded = param.excluded_values ?? [];
    if (excluded.length === 0) return param.values;
    return param.values.filter((v) => !excluded.includes(v));
}

/**
 * Параметры, которые не участвуют в розыгрыше: disabled и все их потомки
 * (если переключатель выключен, зависящие от него параметры смысла не имеют).
 */
export function excludedFromRollNames(params: ModSettingParam[]): Set<string> {
    const names = new Set(params.filter((p) => p.disabled).map((p) => p.name));
    if (names.size === 0) return names;
    let changed = true;
    while (changed) {
        changed = false;
        for (const p of params) {
            if (p.depends_on && names.has(p.depends_on) && !names.has(p.name)) {
                names.add(p.name);
                changed = true;
            }
        }
    }
    return names;
}

/** Случайное значение параметра (по возможности не равное exclude). */
function randomParamValue(param: ModSettingParam, exclude?: string): string {
    const allowed = allowedParamValues(param);
    const pool =
        allowed.length > 1 && exclude !== undefined
            ? allowed.filter((v) => v !== exclude)
            : allowed;
    return (pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : null) ?? settingFallback(param);
}

/**
 * Активные параметры с учётом ветвления: параметр виден, если его родитель
 * тоже активен и у родителя выбрано одно из depends_values. Параметры без
 * ветвления активны всегда.
 */
export function visibleModParams(
    params: ModSetting['params'],
    values: Map<string, string>
): ModSetting['params'] {
    const byName = new Map(params.map((p) => [p.name, p]));
    const active = new Set<string>();
    for (const p of params) if (!p.depends_on) active.add(p.name);

    // Несколько проходов: родитель может быть объявлен ниже ребёнка.
    for (let pass = 0; pass < params.length; pass++) {
        let changed = false;
        for (const p of params) {
            if (!p.depends_on || active.has(p.name)) continue;
            const parent = byName.get(p.depends_on);
            if (!parent || !active.has(parent.name)) continue;
            const value = values.get(parent.name) ?? settingFallback(parent);
            if ((p.depends_values ?? []).includes(value)) {
                active.add(p.name);
                changed = true;
            }
        }
        if (!changed) break;
    }

    return params.filter((p) => active.has(p.name));
}

/** Отбрасывает параметры, не активные при текущих значениях (ветвление). */
function withVisibility(
    modId: number,
    params: ModSettingValue[],
    setting: ModSetting
): ModSettingRoll {
    const values = new Map(params.map((p) => [p.name, p.value]));
    const visible = new Set(visibleModParams(setting.params, values).map((p) => p.name));
    return { modId, params: params.filter((p) => visible.has(p.name)) };
}

/** Случайный пресет настроек (по возможности не тот же, что был). */
export function randomModPreset(
    presets: ModSettingPreset[],
    excludeName?: string | null
): ModSettingPreset | null {
    if (presets.length === 0) return null;
    const pool = presets.length > 1 && excludeName ? presets.filter((p) => p.name !== excludeName) : presets;
    const list = pool.length > 0 ? pool : presets;
    return list[Math.floor(Math.random() * list.length)] ?? null;
}

/**
 * Рандомизирует настройки мода. Предыдущий результат передаётся,
 * чтобы не повторять значения и сохранить включённые пользователем параметры.
 * mode='preset' — вместо случайных значений берём случайный сохранённый пресет.
 * null — если у мода нет настроек.
 */
export function rollModSettings(
    previous: ModSettingRoll | null,
    modId: number | null,
    settings: Map<number, ModSetting>,
    presets: ModSettingPreset[] = [],
    mode?: ModSettingMode
): ModSettingRoll | null {
    const setting = modId == null ? undefined : settings.get(modId);
    if (modId == null || !setting) return null;

    const prev = previous && previous.modId === modId ? previous : null;
    // Без пресетов режим preset невозможен
    const nextMode: ModSettingMode = presets.length === 0 ? 'random' : (mode ?? prev?.mode ?? 'random');

    if (nextMode === 'preset') {
        const preset = randomModPreset(presets, prev?.presetName ?? null);
        if (preset) return presetToRoll(modId, preset, settings);
    }

    const prevByName = new Map((prev?.params ?? []).map((p) => [p.name, p]));

    // Параметры, исключённые модератором, в розыгрыш не попадают вовсе.
    const rollExcluded = excludedFromRollNames(setting.params);
    const params = setting.params
        .filter((param) => !rollExcluded.has(param.name))
        .map((param) => {
            const prevValue = prevByName.get(param.name);
            // Включённые пользователем параметры остаются включёнными при «Заново»
            const enabled = param.randomize !== false || prevValue?.enabled === true;
            if (!enabled) {
                return { name: param.name, value: settingFallback(param), enabled: false };
            }
            return {
                name: param.name,
                value: randomParamValue(param, prevValue?.value),
                enabled: true,
            };
        });

    return { ...withVisibility(modId, params, setting), mode: 'random', presetName: null };
}

/**
 * Переключает параметр: выключенный включается со случайным значением,
 * включённый выключается и возвращается к дефолту.
 * null — если параметр/мод не найден.
 */
export function toggleModSetting(
    roll: ModSettingRoll,
    name: string,
    settings: Map<number, ModSetting>
): ModSettingRoll | null {
    const setting = settings.get(roll.modId);
    const param = setting?.params.find((p) => p.name === name);
    // Исключённые из рандомизации параметры в результате не показываются
    if (!setting || !param || param.disabled) return null;

    const params = roll.params.map((item) => {
        if (item.name !== name) return item;
        if (item.enabled) {
            return { ...item, value: settingFallback(param), enabled: false };
        }
        // Включаем: сразу рандомное значение, не повторяя дефолт
        return {
            ...item,
            value: randomParamValue(param, settingFallback(param)),
            enabled: true,
        };
    });

    return {
        ...withVisibility(roll.modId, params, setting),
        mode: roll.mode ?? 'random',
        presetName: roll.presetName ?? null,
    };
}

/** Подставляет значения пресета: показываем только параметры, которые в нём есть. */
export function presetToRoll(
    modId: number | null,
    preset: ModSettingPreset,
    settings: Map<number, ModSetting>
): ModSettingRoll | null {
    const setting = modId == null ? undefined : settings.get(modId);
    if (modId == null || !setting) return null;

    // Параметры, не указанные в пресете, не показываем вовсе
    const params = setting.params
        .filter((param) => preset.values[param.name] !== undefined)
        .map((param) => ({
            name: param.name,
            value: preset.values[param.name] ?? settingFallback(param),
            enabled: true,
        }));

    return { ...withVisibility(modId, params, setting), mode: 'preset', presetName: preset.name };
}

export interface ModSettingsBoxHandlers {
    /** Перерандомизировать значения (кнопка «🎲 Заново»). */
    onReroll?: () => void;
    /** Применить пресет настроек (клик по чипу). */
    onPreset?: (preset: ModSettingPreset) => void;
    /** Включить/выключить параметр (клик по строке в блоке выключенных). */
    onToggle?: (name: string) => void;
    /** Переключить источник значений: случайные параметры / случайный пресет. */
    onMode?: (mode: ModSettingMode) => void;
    /** Открыть окно исключений из рандомизации (только для модераторов). */
    onExclusions?: () => void;
    /** Сколько исключений действует у мода — для бейджа на кнопке. */
    exclusions?: ModExclusionsCount;
}

/** Блок «Настройки мода»: активные параметры, выключенные, «Заново» и пресеты. */
export function buildModSettingsBox(
    roll: ModSettingRoll,
    modName: string,
    presets: ModSettingPreset[],
    handlers: ModSettingsBoxHandlers = {}
): HTMLElement {
    const box = document.createElement('div');
    box.className = 'rr-mod-settings';

    const head = document.createElement('div');
    head.className = 'rr-mod-settings-head';

    const title = document.createElement('span');
    title.className = 'rr-mod-settings-title';
    title.append(t('tools.random_mod_settings') + ': ');
    const modLabel = document.createElement('b');
    modLabel.textContent = modName;
    title.appendChild(modLabel);
    head.appendChild(title);

    // Кнопки справа единым блоком (иначе space-between разносил их по всей шапке)
    const actions = document.createElement('div');
    actions.className = 'rr-mod-settings-head-actions';

    // Кнопка исключений — только у модераторов (handler передаётся вызывающей страницей)
    if (handlers.onExclusions) {
        const count = handlers.exclusions?.total ?? 0;
        const excl = document.createElement('button');
        excl.type = 'button';
        // Красная всегда; ярче — когда что-то реально исключено
        excl.className = 'btn-secondary rr-mod-settings-exclusions' + (count > 0 ? ' is-active' : '');
        excl.title =
            count > 0
                ? t('mods.exclusions_button_active', { n: count })
                : t('mods.exclusions_button_none');

        const icon = document.createElement('span');
        icon.className = 'rr-mod-settings-exclusions-icon';
        icon.textContent = '⊘';
        icon.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.textContent = t('mods.exclusions_button');
        excl.append(icon, label);

        if (count > 0) {
            const badge = document.createElement('span');
            badge.className = 'rr-mod-settings-exclusions-badge';
            badge.textContent = String(count);
            excl.appendChild(badge);
        }

        excl.addEventListener('click', () => handlers.onExclusions?.());
        actions.appendChild(excl);
    }

    if (handlers.onReroll) {
        const reroll = document.createElement('button');
        reroll.type = 'button';
        reroll.className = 'btn-secondary rr-mod-settings-reroll';
        reroll.textContent = t('tools.random_settings_reroll');
        reroll.addEventListener('click', () => handlers.onReroll?.());
        actions.appendChild(reroll);
    }

    if (actions.childElementCount > 0) head.appendChild(actions);
    box.appendChild(head);

    // Переключатель источника значений — только если у мода есть пресеты
    const mode = roll.mode ?? 'random';
    if (presets.length > 0 && handlers.onMode) {
        const bar = document.createElement('div');
        bar.className = 'rr-mod-settings-mode';

        const option = (value: ModSettingMode, label: string, active: boolean): HTMLElement => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'rr-mod-settings-mode-opt' + (active ? ' is-active' : '');
            btn.textContent = label;
            btn.title = t('tools.random_settings_mode_hint');
            btn.addEventListener('click', () => {
                if (!active) handlers.onMode?.(value);
            });
            return btn;
        };

        bar.appendChild(option('random', t('tools.random_settings_mode_random'), mode !== 'preset'));
        // В режиме пресета показываем, какой именно пресет выпал
        const presetLabel =
            mode === 'preset' && roll.presetName
                ? `${t('tools.random_settings_mode_preset')}: ${roll.presetName}`
                : t('tools.random_settings_mode_preset');
        bar.appendChild(option('preset', presetLabel, mode === 'preset'));
        box.appendChild(bar);
    }

    // Строка настройки: номер (по порядку) → название → значение
    const row = (param: ModSettingValue, index: number | null, interactive: boolean): HTMLElement => {
        const clickable = interactive && handlers.onToggle;
        const el = clickable ? document.createElement('button') : document.createElement('div');
        el.className = 'rr-mod-setting' + (param.enabled ? '' : ' rr-mod-setting--off');
        if (clickable) {
            (el as HTMLButtonElement).type = 'button';
            el.title = t('tools.random_settings_off_hint');
            el.addEventListener('click', () => handlers.onToggle?.(param.name));
        }

        const num = document.createElement('span');
        num.className = 'rr-mod-setting-index';
        num.textContent = index === null ? '•' : String(index);
        el.appendChild(num);

        const name = document.createElement('span');
        name.className = 'rr-mod-setting-name';
        name.textContent = param.name;
        name.title = param.name;
        el.appendChild(name);

        const value = document.createElement('span');
        value.className = 'rr-mod-setting-value';
        value.textContent = param.value;
        value.title = param.value;
        el.appendChild(value);

        return el;
    };

    const enabled = roll.params.filter((p) => p.enabled);
    const disabled = roll.params.filter((p) => !p.enabled);

    // Активные настройки — по порядку набора, с нумерацией
    const params = document.createElement('div');
    params.className = 'rr-mod-settings-params';
    enabled.forEach((param, i) => params.appendChild(row(param, i + 1, false)));
    if (enabled.length > 0) box.appendChild(params);

    // Выключенные параметры — отдельно ниже, приглушённые; клик включает
    if (disabled.length > 0) {
        const off = document.createElement('div');
        off.className = 'rr-mod-settings-off';

        const label = document.createElement('span');
        label.className = 'rr-mod-settings-off-label';
        label.textContent = t('tools.random_settings_off');
        off.appendChild(label);

        const list = document.createElement('div');
        list.className = 'rr-mod-settings-off-list';
        for (const param of disabled) list.appendChild(row(param, null, true));
        off.appendChild(list);

        box.appendChild(off);
    }

    // Пресеты настроек: клик подставляет сохранённые значения
    if (presets.length > 0 && handlers.onPreset) {
        const list = document.createElement('div');
        list.className = 'rr-mod-settings-presets';

        const label = document.createElement('span');
        label.textContent = t('tools.random_settings_presets');
        list.appendChild(label);

        for (const preset of presets) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'rr-preset-chip';
            chip.textContent = preset.name;
            const values = Object.entries(preset.values).map(([k, v]) => `${k}: ${v}`);
            if (values.length > 0) chip.title = values.join(', ');
            chip.addEventListener('click', () => handlers.onPreset?.(preset));
            list.appendChild(chip);
        }

        box.appendChild(list);
    }

    return box;
}
